import { MobileOfferAssignment, MobileOfferAssignmentStatus, MobileOfferCustomer } from '@numeo/types'
import { ArrowLeft, CheckCircle2, Mail, Phone, UserRound } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { mobileOfferError, mobileOffersApi } from './api'
import { CustomerAssignmentForm, CustomerAssignmentResult } from './CustomerAssignmentForm'
import { CustomerAssignmentHistory } from './CustomerAssignmentHistory'
import { customerOfferDate, customerOfferLinkClass, customerOfferSecondaryClass, customerPlanName, customerStoreName } from './customer-offer-utils'
import { OfferButton, OfferNotice } from './ui'

function CustomerOfferContent({ customer, initialOfferId }: { customer: MobileOfferCustomer; initialOfferId?: string }) {
    const [result, setResult] = useState<CustomerAssignmentResult | null>(null)
    const [refreshToken, setRefreshToken] = useState(0)
    const updateResult = useCallback((record: MobileOfferAssignment) => {
        setResult((current) =>
            current?.assignment.id === record.id && (record.revision ?? 0) >= (current.assignment.revision ?? 0)
                ? {
                      ...current,
                      assignment: record,
                      deliveryConfirmed: current.deliveryConfirmed && (record.revision ?? 0) === (current.assignment.revision ?? 0),
                  }
                : current
        )
    }, [])
    const refreshHistory = () => setRefreshToken((value) => value + 1)
    const assigned = result?.assignment.status === MobileOfferAssignmentStatus.ASSIGNED
    return (
        <div className="space-y-5">
            <Link to="/billing/mobile-offers/customers" className={customerOfferLinkClass}>
                <ArrowLeft className="h-4 w-4" />
                Back to customers
            </Link>
            <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
                <span className="rounded-xl bg-slate-100 p-3 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    <UserRound className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                    <h1 className="text-2xl font-semibold">{customer.name || 'Customer'}</h1>
                    <div className={`mt-2 flex flex-wrap gap-x-5 gap-y-2 ${customerOfferSecondaryClass}`}>
                        <span className="inline-flex items-center gap-2 break-all">
                            <Mail className="h-4 w-4 shrink-0" />
                            {customer.email || 'No signup email'}
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0" />
                            {customer.phone || 'No phone number'}
                        </span>
                    </div>
                    <p className="mt-2 break-all text-xs text-slate-500 dark:text-slate-400">Account {customer.userId}</p>
                </div>
            </div>
            <OfferNotice>Identity information only. This record does not establish the customer’s subscription status or offer eligibility.</OfferNotice>
            {result && (
                <section
                    role="status"
                    className="space-y-3 rounded-xl border border-blue-200 bg-blue-50 p-5 text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                >
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <h2 className="font-semibold">{assigned && result.deliveryConfirmed ? 'Offer assigned' : `Assignment ${result.assignment.status}`}</h2>
                    </div>
                    <p className="text-sm">
                        {assigned
                            ? result.deliveryConfirmed
                                ? 'The customer can access this offer when their mobile billing page refreshes, subject to eligibility and store availability. No subscription has been activated.'
                                : 'An assignment record exists. Store delivery could not be confirmed; review the history before retrying the same offer. No paid access is granted by this record.'
                            : result.assignment.status === MobileOfferAssignmentStatus.REVOKED
                              ? 'This assignment has been revoked. No subscription was canceled. An Apple code already disclosed may remain redeemable.'
                              : 'This is the latest recorded assignment status. Existing subscription access is determined separately.'}
                    </p>
                    <p className="text-sm">
                        {customerPlanName(result.assignment.plan)} · {customerStoreName(result.assignment.provider)} · Redeem before{' '}
                        {customerOfferDate(result.assignment.redeemBy)}
                    </p>
                    <a href="#customer-assignment-history" className={customerOfferLinkClass}>
                        View assignment history
                    </a>
                </section>
            )}
            <CustomerAssignmentForm customer={customer} initialOfferId={initialOfferId} onResult={setResult} onHistoryRefresh={refreshHistory} />
            <CustomerAssignmentHistory userId={customer.userId} refreshToken={refreshToken} onStatusChanged={updateResult} onReassigned={setResult} />
        </div>
    )
}

export function CustomerOfferPage() {
    const { userId } = useParams<{ userId: string }>()
    const [params] = useSearchParams()
    const [customer, setCustomer] = useState<MobileOfferCustomer | null>(null)
    const [error, setError] = useState('')
    const [attempt, setAttempt] = useState(0)
    useEffect(() => {
        let cancelled = false
        setCustomer(null)
        setError('')
        if (!userId) {
            setError('Select a customer from an exact identity search.')
            return
        }
        void mobileOffersApi
            .customer(userId)
            .then((record) => {
                if (!cancelled) setCustomer(record)
            })
            .catch((failure) => {
                if (!cancelled) setError(mobileOfferError(failure))
            })
        return () => {
            cancelled = true
        }
    }, [userId, attempt])
    if (!customer || customer.userId !== userId)
        return (
            <div className="space-y-4">
                <Link className={customerOfferLinkClass} to="/billing/mobile-offers/customers">
                    <ArrowLeft className="h-4 w-4" />
                    Back to customers
                </Link>
                {error ? (
                    <>
                        <OfferNotice error>{error}</OfferNotice>
                        <OfferButton variant="secondary" onClick={() => setAttempt((value) => value + 1)}>
                            Retry customer
                        </OfferButton>
                    </>
                ) : (
                    <OfferNotice>Loading customer identity…</OfferNotice>
                )}
            </div>
        )
    return <CustomerOfferContent key={customer.userId} customer={customer} initialOfferId={params.get('offerId') || undefined} />
}
